import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { TASK_PRIORITY_LABEL } from "@/lib/enumLabels";

/*
  Iter 85.63 · Shared canonical Task editor.
  ──────────────────────────────────────────
  One dialog, one PUT, one source of truth. Wired into every surface where
  the canonical Task appears — global Tasks board, Engagement Tasks tab,
  Activity task strip, etc. Every field on this form maps 1:1 to the
  canonical `tasks` collection and hits `PUT /api/tasks/{id}`, so a save
  from ANY surface propagates instantly to every other view because the
  server is the source of truth.

  Props:
    open, onOpenChange : controlled dialog state
    task               : the canonical task doc (null = closed / not ready)
    engagements        : list of engagement docs for the picker
    activities         : list of activity notes for the picker (optional)
    users              : list of user docs for the assignee picker
    onSaved            : (updatedTask) => void — parent refetches board
    onDeleted          : (deletedId)   => void — optional
*/

const PRIORITIES = ["low", "medium", "high", "urgent"];
// Iter 85.63.1 · Align dialog statuses with Kanban columns so a partner
// can move a task between any of the five columns via the editor
// (previously only open/in_progress/done, forcing drag-and-drop for the
// other three). Order mirrors the board left-to-right.
const STATUSES = ["backlog", "todo", "in_progress", "review", "done"];

export default function TaskEditDialog({
  open, onOpenChange, task,
  engagements = [], activities = [], users = [],
  onSaved, onDeleted,
}) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!task) { setForm(null); return; }
    setForm({
      title: task.title || "",
      description: task.description || "",
      priority: task.priority || "medium",
      status: task.status || "open",
      due_date: task.due_date || "",
      assignee_id: task.assignee_id || "",
      engagement_id: task.engagement_id || "",
      activity_briefing_id: task.activity_briefing_id || "",
    });
  }, [task]);

  if (!form || !task) return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none bg-card" data-testid="task-edit-dialog-empty" />
    </Dialog>
  );

  const scopedActivities = form.engagement_id
    ? activities.filter((a) => a.engagement_id === form.engagement_id)
    : [];

  const commit = async () => {
    if (saving) return;
    const patch = {
      title: form.title.trim(),
      description: form.description || "",
      priority: form.priority,
      status: form.status,
      due_date: form.due_date || null,
      assignee_id: form.assignee_id || null,
      engagement_id: form.engagement_id || null,
      activity_briefing_id: form.activity_briefing_id || null,
    };
    if (!patch.title) { toast.error("Title required"); return; }
    setSaving(true);
    try {
      const { data } = await api.put(`/tasks/${task.id}`, patch);
      toast.success("Task updated");
      onOpenChange(false);
      onSaved?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!confirm(`Delete "${task.title}"?`)) return;
    try {
      await api.delete(`/tasks/${task.id}`);
      toast.success("Task removed");
      onOpenChange(false);
      onDeleted?.(task.id);
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none bg-card max-w-2xl" data-testid="task-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Edit task</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Input
            data-testid="task-edit-title"
            placeholder="Title"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="rounded-none bg-secondary/40"
          />
          <Textarea
            data-testid="task-edit-description"
            placeholder="Details / description"
            rows={4}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="rounded-none bg-secondary/40"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="text-[0.5938rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">Status</div>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="task-edit-status" className="rounded-none bg-secondary/40"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <div className="text-[0.5938rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">Priority</div>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="task-edit-priority" className="rounded-none bg-secondary/40"><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{TASK_PRIORITY_LABEL[p] || p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <div className="text-[0.5938rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">Due date</div>
              <Input
                type="date"
                value={form.due_date || ""}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                data-testid="task-edit-due"
                className="rounded-none bg-secondary/40"
              />
            </div>
            <div>
              <div className="text-[0.5938rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">Assignee</div>
              <Select value={form.assignee_id || "none"} onValueChange={(v) => setForm({ ...form, assignee_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="task-edit-assignee" className="rounded-none bg-secondary/40"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <div className="text-[0.5938rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">Engagement</div>
              <Select value={form.engagement_id || "none"} onValueChange={(v) => setForm({ ...form, engagement_id: v === "none" ? "" : v, activity_briefing_id: "" })}>
                <SelectTrigger data-testid="task-edit-engagement" className="rounded-none bg-secondary/40"><SelectValue placeholder="(No engagement)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">(No engagement)</SelectItem>
                  {engagements.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {form.engagement_id && scopedActivities.length > 0 && (
              <div className="sm:col-span-2">
                <div className="text-[0.5938rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">Activity briefing (optional)</div>
                <Select value={form.activity_briefing_id || "none"} onValueChange={(v) => setForm({ ...form, activity_briefing_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="task-edit-activity" className="rounded-none bg-secondary/40"><SelectValue placeholder="(No activity)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">(No activity)</SelectItem>
                    {scopedActivities.map((a) => (
                      <SelectItem key={a.id} value={a.id}>{a.title}{a.activity_date ? ` · ${a.activity_date}` : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="flex items-center justify-between sm:justify-between gap-2">
          <Button
            variant="ghost"
            onClick={remove}
            data-testid="task-edit-delete"
            className="rounded-none text-destructive hover:bg-destructive/10"
          >
            Delete
          </Button>
          <Button
            data-testid="task-edit-save"
            onClick={commit}
            disabled={saving}
            className="rounded-none bg-primary text-primary-foreground"
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
